<script lang="ts">
  import QueryStatus from '$lib/components/ui/QueryStatus.svelte';
  import { scheduleUndo } from '$lib/state/actionDialog.svelte';
  import { createQuery, createMutation, useQueryClient } from '@tanstack/svelte-query';
  import { Building2, Trash2, Pencil, Upload, ChevronRight } from 'lucide-svelte';
  import { companiesApi } from '$lib/api/crm';
  import { session } from '$lib/state/session.svelte';
  import { route } from '$lib/state/route.svelte';
  import { toast } from '$lib/state/toast.svelte';
  import { getErrorMessage } from '$lib/api/client';
  import SearchBar from '$lib/components/ui/SearchBar.svelte';
  import Pagination from '$lib/components/ui/Pagination.svelte';
  import ImportWizard from '$lib/components/ImportWizard.svelte';
  import CompanyDetail from './CompanyDetail.svelte';
  import CompanyFormModal from './CompanyFormModal.svelte';
  import type { Company } from '$lib/types';

  const queryClient = useQueryClient();
  let showImport = $state(false);

  let search = $state('');
  let page = $state(1);
  let kind = $state('');
  let partnerStatus = $state('');
  let showForm = $state(false);
  let editingCompany = $state<Company | null>(null);
  // Clicking a row opens the detail view; editing is a separate affordance
  // (the row's pencil icon, or the "Edit" button inside the detail view).
  // The selected id lives in the URL hash (#/companies/<id>), not local
  // state, so browser Back/Forward and refresh both work.
  const detailCompanyId = $derived(route.current.tab === 'companies' ? (route.current.id ?? null) : null);

  // "hotel" is the Company.Kind value (internal/models/crm.go) but means
  // "property" in Hotels-vertical UX terms (a single physical hotel, as
  // opposed to its brand or its owner group) — labelled "Property" here and
  // everywhere else a kind badge/chip is shown, so "Hotels vertical" (the
  // business unit) is never confused with "Hotel kind" (one property).
  const kindChips: Array<{ value: string; label: string }> = [
    { value: '', label: 'All' },
    { value: 'hotel', label: 'Properties' },
    { value: 'brand', label: 'Brands' },
    { value: 'owner', label: 'Owners' }
  ];
  const KIND_LABEL: Record<string, string> = {
    hotel: 'Property',
    brand: 'Brand',
    owner: 'Owner',
    company: 'Company'
  };
  function kindLabel(k: string): string {
    return KIND_LABEL[k] ?? k;
  }

  // ponytail: badge colors ported verbatim from the mockup's `kindStyle` map.
  const KIND_BADGE: Record<string, { bg: string; fg: string }> = {
    hotel: { bg: '#E3F0F1', fg: '#226A73' },
    brand: { bg: '#ECF8CC', fg: '#5c7a00' },
    owner: { bg: '#FBF0DC', fg: '#8a5c00' },
    company: { bg: '#F1F0EF', fg: '#666666' }
  };
  function kindBadge(k: string) {
    return KIND_BADGE[k] ?? KIND_BADGE.company;
  }

  const partnerStatuses: Array<{ value: string; label: string }> = [
    { value: 'prospect', label: 'Prospect' },
    { value: 'in_discussion', label: 'In discussion' },
    { value: 'active_partner', label: 'Active partner' },
    { value: 'dormant', label: 'Dormant' }
  ];
  const PARTNER_BADGE: Record<string, { bg: string; fg: string; label: string }> = {
    prospect: { bg: '#F1F0EF', fg: '#666666', label: 'Prospect' },
    in_discussion: { bg: '#FBF0DC', fg: '#8a5c00', label: 'In discussion' },
    active_partner: { bg: '#ECF8CC', fg: '#5c7a00', label: 'Active partner' },
    dormant: { bg: '#E6E9EE', fg: '#10223A', label: 'Dormant' }
  };

  const verticalId = $derived(
    session.selectedVertical === 'all' ? undefined : session.selectedVertical
  );
  // The kind filter (Properties/Brands/Owners) only means anything in the
  // Hotels vertical — every other vertical's companies are all kind=company,
  // so the chips would just be dead UI there (and confusing when an admin
  // has "All verticals" selected, since kind then spans verticals).
  const listIsHotels = $derived(
    session.me?.verticals.find((v) => v.id === verticalId)?.slug === 'hotels'
  );

  const listQuery = createQuery(() => ({
    queryKey: ['companies', { search, page, kind, partnerStatus, verticalId }],
    queryFn: () =>
      companiesApi.list({
        q: search || undefined,
        page,
        per_page: 25,
        kind: kind || undefined,
        partner_status: partnerStatus || undefined,
        vertical_id: verticalId
      })
  }));

  $effect(() => {
    search;
    kind;
    partnerStatus;
    verticalId;
    page = 1;
  });
  // The kind filter only applies within the Hotels vertical (see
  // listIsHotels) — drop a stale value when switching to any other vertical
  // so the list doesn't silently filter to zero results.
  $effect(() => {
    verticalId;
    kind = '';
  });

  const deleteMutation = createMutation(() => ({
    mutationFn: (id: number) => companiesApi.remove(id),
    onError: (err: unknown) => toast.error(getErrorMessage(err)),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['companies'] })
  }));

  function openCreate() {
    editingCompany = null;
    showForm = true;
  }

  function openEdit(row: Company) {
    editingCompany = row;
    showForm = true;
  }

  function openDetail(row: Company) {
    route.push({ tab: 'companies', id: row.id });
  }
  function closeDetail() {
    route.push({ tab: 'companies' });
  }

  function closeForm() {
    showForm = false;
    editingCompany = null;
  }

  // Consumes the Header's "+ New company" / topbar-search-result signal:
  // opens the create form, or the edit form pre-filled with the exact row
  // the search already fetched (see session.svelte.ts's PageSignal doc).
  $effect(() => {
    const signal = session.consumeSignal('companies');
    if (!signal) return;
    if (signal.mode === 'create') openCreate();
    else openEdit(signal.record as Company);
  });

  function remove(row: Company) {
    scheduleUndo(`Delete “${row.name}”`, () => deleteMutation.mutate(row.id));
  }
</script>

{#if detailCompanyId}
  <CompanyDetail
    id={detailCompanyId}
    onClose={closeDetail}
    onInvalid={() => route.replace({ tab: 'companies' })}
    onNavigate={(navId) => route.push({ tab: 'companies', id: navId })}
    onEdit={openEdit}
    onOpenSite={(siteId, siteVerticalId) => route.push({ tab: 'sites', id: siteId, vertical: String(siteVerticalId) })}
  />
{:else}
<div class="tym-page">
  <div class="mb-4 flex flex-wrap items-center gap-2">
    {#if listIsHotels}
      {#each kindChips as chip (chip.value)}
        <button
          type="button"
          class="tym-chip {kind === chip.value ? 'active' : ''}"
          onclick={() => (kind = chip.value)}
        >
          {chip.label}
        </button>
      {/each}
    {/if}
    <select class="input w-auto" bind:value={partnerStatus} aria-label="Filter by partner status">
      <option value="">All partner statuses</option>
      {#each partnerStatuses as ps (ps.value)}
        <option value={ps.value}>{ps.label}</option>
      {/each}
    </select>
    <span class="flex-1"></span>
    <span class="text-[12.5px] text-dark-gray">{listQuery.data?.count ?? 0} companies</span>
  </div>

  <div class="flex items-start gap-3">
    <div class="min-w-0 flex-1">
      <SearchBar bind:value={search} onCreate={openCreate} createLabel="New company" />
    </div>
    <button type="button" class="btn-secondary shrink-0" onclick={() => (showImport = true)} aria-label="Import">
      <Upload class="h-4 w-4" />
      <span class="hidden sm:inline">Import</span>
    </button>
  </div>

  {#if listQuery.isLoading}
    <QueryStatus loading label="companies" />
  {:else if listQuery.isError}
    <QueryStatus error={listQuery.error} label="companies" onRetry={() => listQuery.refetch()} />
  {:else if listQuery.data}
    <!-- desktop/tablet table; below sm it gives way to the card list -->
    <div class="tym-table-wrap tym-shadow hidden sm:block">
      <div class="tym-table-scroll">
        <table class="tym-table">
          <thead>
            <tr>
              <th>Company</th>
              <th>Kind</th>
              <th>Partner status</th>
              <th>City</th>
              <th>Phone</th>
              <th>Email</th>
              <th class="num"></th>
            </tr>
          </thead>
          <tbody>
            {#each listQuery.data.results as row (row.id)}
              {@const badge = kindBadge(row.kind)}
              {@const partnerBadge = row.partner_status ? PARTNER_BADGE[row.partner_status] : null}
              <tr class="clickable" onclick={() => openDetail(row)}>
                <td>
                  <div class="flex items-center gap-[11px]">
                    <span
                      class="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[9px]"
                      style="background:#E3F0F1; color:#226A73"
                    >
                      <Building2 class="h-4 w-4" />
                    </span>
                    <div class="min-w-0">
                      <span class="block text-[13.5px] font-semibold text-navy">{row.name}</span>
                      {#if row.owner_name || row.brand_name}
                        <span class="block truncate text-[11px] text-dark-gray">
                          {#if row.owner_name}Owner: {row.owner_name}{/if}{#if row.owner_name && row.brand_name} &middot; {/if}{#if row.brand_name}Brand: {row.brand_name}{/if}
                        </span>
                      {/if}
                    </div>
                  </div>
                </td>
                <td>
                  <span class="tym-badge" style="background:{badge.bg}; color:{badge.fg}"
                    >{kindLabel(row.kind)}</span
                  >
                </td>
                <td>
                  {#if partnerBadge}
                    <span class="tym-badge" style="background:{partnerBadge.bg}; color:{partnerBadge.fg}"
                      >{partnerBadge.label}</span
                    >
                  {:else}
                    <span class="muted">—</span>
                  {/if}
                </td>
                <td class="muted">{row.city}</td>
                <td class="muted">{row.phone}</td>
                <td class="muted">{row.email}</td>
                <td class="num">
                  <div class="flex justify-end gap-1.5">
                    <button
                      type="button"
                      class="btn-secondary !h-10 !w-10 !p-0"
                      onclick={(e) => {
                        e.stopPropagation();
                        openEdit(row);
                      }}
                      aria-label={`Edit ${row.name}`}
                    >
                      <Pencil class="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      class="btn-danger !h-10 !w-10 !p-0"
                      onclick={(e) => {
                        e.stopPropagation();
                        remove(row);
                      }}
                      aria-label={`Delete ${row.name}`}
                    >
                      <Trash2 class="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            {:else}
              <tr><td class="empty" colspan="7">No companies found.</td></tr>
            {/each}
          </tbody>
        </table>
      </div>
    </div>

    <!-- mobile card list — a 7-column table is unreadable at 390px -->
    <div class="flex flex-col gap-2.5 sm:hidden">
      {#each listQuery.data.results as row (row.id)}
        {@const badge = kindBadge(row.kind)}
        {@const partnerBadge = row.partner_status ? PARTNER_BADGE[row.partner_status] : null}
        <button type="button" class="tym-list-card" onclick={() => openDetail(row)}>
          <span class="flex items-center gap-3">
            <span
              class="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[9px]"
              style="background:#E3F0F1; color:#226A73"
            >
              <Building2 class="h-4 w-4" />
            </span>
            <span class="min-w-0 flex-1">
              <span class="block truncate text-[14.5px] font-semibold text-navy">{row.name}</span>
              <span class="mt-1 flex flex-wrap items-center gap-1.5">
                <span class="tym-badge" style="background:{badge.bg}; color:{badge.fg}">{kindLabel(row.kind)}</span>
                {#if partnerBadge}
                  <span class="tym-badge" style="background:{partnerBadge.bg}; color:{partnerBadge.fg}"
                    >{partnerBadge.label}</span
                  >
                {/if}
                {#if row.city}<span class="text-[11.5px] text-dark-gray">{row.city}</span>{/if}
              </span>
              {#if row.phone || row.email}
                <span class="mt-1 block truncate text-[11.5px] text-dark-gray">{row.phone || row.email}</span>
              {/if}
            </span>
            <ChevronRight class="h-4 w-4 shrink-0 text-dark-gray" />
          </span>
        </button>
      {:else}
        <div class="tym-card px-4 py-8 text-center text-[13px] text-dark-gray">No companies found.</div>
      {/each}
    </div>

    <Pagination
      page={listQuery.data.page}
      perPage={listQuery.data.per_page}
      count={listQuery.data.count}
      onChange={(p) => (page = p)}
    />
  {/if}
</div>

{#if showForm}
  <CompanyFormModal editing={editingCompany} defaultVerticalId={verticalId} onClose={closeForm} />
{/if}
{#if showImport}
  <ImportWizard onClose={() => (showImport = false)} />
{/if}
{/if}
